import { Box, Button } from '@mui/material'
import { LS_ITEMS, addItemToLS, getItemFromLS } from 'utils/localStorage'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getDistinctColumnValues, getPinnedSelectionsByCol } from 'components/common/PinnedFilters/pinFilterUtils'

import AutoSizer from 'react-virtualized-auto-sizer'
import { Icon as Iconify } from '@iconify/react'
import { VariableSizeList as List } from 'react-window'
import WellRow from './components/WellRow'
import HeaderRow from './components/HeaderRow'
import PinButton from 'components/common/PinnedFilters/PinButton'
import PinnedFilterCarousel from 'components/common/PinnedFilters/PinnedFilterCarousel'
import SearchBarWithRecent from 'components/common/SearchBarWithRecent'
import SelectionCarousel from 'components/common/OffsetWellSelector/SelectionCarousel'
import { appColors } from 'utils'
import { getWellListPinOptions } from 'components/common/PinnedFilters/pinFilterDefs'
import { onlyShowFollowingWellsAtom, currentWellAtom } from 'atoms'
import useDebouncedValue from 'components/common/hooks/useDebouncedValue'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { usePinnedFilters } from 'components/common/hooks/usePinnedFilters'
import { useRecoilState, useRecoilValue } from 'recoil'
import useUpdateWellInfo from 'components/common/hooks/useUpdateWellInfo'
import useWellListContext from 'components/common/hooks/WellListContext'
import useWellListNavigate from 'components/common/hooks/useWellListNavigate'

const LIST_WIDTH = '35vw'
const LIST_HEIGHT = 'calc(100vh - 80px)'
const HEADER_HEIGHT = 44
const ITEM_HEIGHT = 114

const isHeaderRow = (row) => !!row?.header

const WellSelectorList = ({
  // props are only used in multiSelect mode. currently WellSelectorList used outside of multiSelect
  // *only ever changes the current well*
  multiSelect = false,
  selectedWells = [],
  setSelectedWells,
  width = LIST_WIDTH,
  listHeight = LIST_HEIGHT,
}) => {
  const listRef = useRef(null)
  const currentDisplayedWellsRef = useRef([])
  const { navigateToWellPage } = useWellListNavigate()
  const currentWell = useRecoilValue(currentWellAtom)
  const [onlyShowFollowing, setOnlyShowFollowing] = useRecoilState(onlyShowFollowingWellsAtom)
  const [onlyShowPad, setOnlyShowPad] = useState(false)
  const { wellList: fullWellList, getFilteredWellList } = useWellListContext()
  const { searchBarStyle, theme, getActionBarColor, getTextColor } = useInnovaTheme()
  const { getDatabaseOrg } = useInnovaAuth()

  const { getDropDownList } = useUpdateWellInfo()
  const useStateLabel = getDropDownList('countys', 'Texas')?.length > 0

  const [searchText, setSearchText] = useState('')
  const defaultPinnedFilters =
    getItemFromLS(LS_ITEMS.sharedWellListGridFilters, 'pinnedGridFilters')?.filter((filter) => !filter.external) || []
  const [pinnedGridFilters, setPinnedGridFilters] = useState(
    getItemFromLS(LS_ITEMS.wellSelectorFilters, 'pinnedGridFilters') || defaultPinnedFilters,
  )
  const pinned = usePinnedFilters()
  const { activePinned, setMany } = pinned

  useEffect(() => {
    const activePinnedFromLS = getItemFromLS(LS_ITEMS.wellSelectorFilters, 'activePinnedFilters')
    if (activePinnedFromLS) {
      setMany(activePinnedFromLS)
    }
  }, [setMany])

  useEffect(() => {
    addItemToLS(LS_ITEMS.wellSelectorFilters, 'activePinnedFilters', activePinned)
  }, [activePinned])

  const referenceWell = useMemo(() => {
    if (!currentWell) return null
    return fullWellList.find(
      (well) => well.actualWell === currentWell.wellName && well.organization === currentWell.organization,
    )
  }, [currentWell, fullWellList])

  const pinOptions = useMemo(() => {
    return getWellListPinOptions(useStateLabel).filter((option) => !option.external)
  }, [useStateLabel])

  const debouncedSearchText = useDebouncedValue(searchText, 100)

  const { flatList: reducedWellList, allowedColumnValues } = useMemo(() => {
    return getFilteredWellList({
      searchText: debouncedSearchText,
      activeFilters: getPinnedSelectionsByCol({ pinOptions, activePinned }),
      pinOptions,
      onlyShowFollowing,
      homeOrgOnly: true,
      refWell: {
        wellName: referenceWell?.actualWell,
        organization: referenceWell?.organization,
      },
      onlyShowPad,
    })
  }, [
    getFilteredWellList,
    debouncedSearchText,
    activePinned,
    pinOptions,
    onlyShowFollowing,
    onlyShowPad,
    referenceWell,
  ])

  useEffect(() => {
    if (listRef.current?.resetAfterIndex) {
      listRef.current.resetAfterIndex(0, true)
    }

    if (!Array.isArray(reducedWellList)) return
    currentDisplayedWellsRef.current = reducedWellList.filter((well) => !isHeaderRow(well))
  }, [reducedWellList])

  const toggleWell = useCallback(
    (row) => {
      setSelectedWells((prev) => {
        if (
          prev.some(
            (selectedWell) =>
              selectedWell.wellName === row.actualWell && selectedWell.organization === row.organization,
          )
        ) {
          return prev.filter(
            (selectedWell) =>
              !(selectedWell.wellName === row.actualWell && selectedWell.organization === row.organization),
          )
        }
        return [...prev, { wellName: row.actualWell, organization: row.organization }]
      })
    },
    [setSelectedWells],
  )

  const selectWell = useCallback(
    (event, item) => {
      if (multiSelect) {
        toggleWell(item)
        return
      }
      navigateToWellPage({ event, url: '', well: item }) // '' -> stay on current page
    },
    [multiSelect, toggleWell, navigateToWellPage],
  )

  const SELECTION_OPTIONS = useMemo(
    () => [
      {
        key: 'unselectAll',
        toolTip: 'Unselect All',
        icon: 'ic:baseline-deselect',
        onClick: () => {
          setSelectedWells([])
        },
      },
      {
        key: 'selectAll',
        toolTip: 'Select All',
        icon: 'ic:baseline-select-all',
        onClick: () => {
          setSelectedWells(
            currentDisplayedWellsRef.current.map((well) => ({
              wellName: well.actualWell,
              organization: well.organization,
            })),
          )
        },
      },
    ],
    [setSelectedWells],
  )

  const Row = useCallback(
    ({ index, style }) => {
      const row = reducedWellList[index]
      if (!row) return null

      if (isHeaderRow(row)) {
        return <HeaderRow row={row} style={style} getActionBarColor={getActionBarColor} />
      }

      const selected =
        multiSelect &&
        selectedWells.some(
          (selectedWell) => selectedWell.wellName === row.actualWell && selectedWell.organization === row.organization,
        )

      return <WellRow row={row} style={style} onSelect={selectWell} theme={theme} selected={selected} />
    },
    [reducedWellList, selectWell, theme, getActionBarColor, multiSelect, selectedWells],
  )

  const getItemSize = useCallback(
    (index) => (isHeaderRow(reducedWellList[index]) ? HEADER_HEIGHT : ITEM_HEIGHT),
    [reducedWellList],
  )

  const homeOrgWellList = useMemo(
    () => fullWellList.filter((well) => well.organization === getDatabaseOrg()),
    [fullWellList, getDatabaseOrg],
  )

  const distinctColumnValues = useMemo(
    () => getDistinctColumnValues({ pinOptions, data: homeOrgWellList }),
    [homeOrgWellList, pinOptions],
  )

  const handleSetPinnedGridFilters = useCallback(
    (newPinned) => {
      setPinnedGridFilters(newPinned)
      addItemToLS(LS_ITEMS.wellSelectorFilters, 'pinnedGridFilters', newPinned)
    },
    [setPinnedGridFilters],
  )

  const handleClearActivePinnedFilters = useCallback(() => {
    pinned?.clear()
    addItemToLS(LS_ITEMS.wellSelectorFilters, 'activePinnedFilters', [])
  }, [pinned])

  const activePinnedFiltersCount = useMemo(
    () => Object.values(activePinned).filter((val) => val).length,
    [activePinned],
  )

  return (
    <Box sx={{ width: width, background: getActionBarColor(), display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          height: 50,
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          pl: 1,
          pr: 2,
        }}>
        <SearchBarWithRecent
          autoFocus
          value={searchText}
          onChange={(term, isWell) => {
            if (isWell) {
              const match = reducedWellList.find((r) => r?.actualWell === term && !isHeaderRow(r))
              if (match) return selectWell(null, match)
            }
            setSearchText(term)
          }}
          onCancelSearch={() => setSearchText('')}
          style={searchBarStyle()}
          recentKey='wellListRecentSearches'
        />

        <Button
          onClick={() => setOnlyShowFollowing(!onlyShowFollowing)}
          title={onlyShowFollowing ? 'Filtered for only Following Wells' : 'Show Only Following Wells'}
          sx={{
            minWidth: 0,
            width: 28,
            height: 28,
            p: 0,
            borderRadius: 1.5,
            lineHeight: 1.2,
          }}>
          {onlyShowFollowing ? (
            <Iconify icon='uim:favorite' style={{ color: 'gold' }} height={20} />
          ) : (
            <Iconify icon='uil:favorite' style={{ color: getTextColor() }} height={20} />
          )}
        </Button>

        {referenceWell && (
          <Button
            onClick={() => setOnlyShowPad((prev) => !prev)}
            title={onlyShowPad ? 'Filtered for Wells on Pad' : 'Show Wells on Pad'}
            disabled={!referenceWell}
            sx={{
              minWidth: 0,
              width: 28,
              height: 28,
              p: 0,
              borderRadius: 1.5,
              lineHeight: 1.2,
            }}>
            {onlyShowPad ? (
              <Iconify icon='bxs:grid' style={{ color: appColors.itemTextColor }} height={20} />
            ) : (
              <Iconify
                icon='bxs:grid'
                style={{ color: !referenceWell ? appColors.borderColorDark : getTextColor() }}
                height={20}
              />
            )}
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pl: 1 }}>
        <PinButton
          name='Filters'
          icon={<Iconify icon='eos-icons:pin' width='16' height='16' />}
          pinnedItemOptions={distinctColumnValues}
          pinnedGridFilters={pinnedGridFilters}
          setPinnedGridFilters={handleSetPinnedGridFilters}
          pinnedFiltersActive={activePinnedFiltersCount}
          onClear={handleClearActivePinnedFilters}
        />
        <PinnedFilterCarousel
          pinnedItemOptions={distinctColumnValues}
          pinnedGridFilters={pinnedGridFilters}
          activePinned={activePinned}
          allowedChips={homeOrgWellList?.length !== reducedWellList?.length ? allowedColumnValues : undefined}
          pinned={pinned}
          maxWidth={`calc(${width} - 55px)`}
        />
      </Box>

      {multiSelect && (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pl: 1 }}>
          <SelectionCarousel options={SELECTION_OPTIONS} />
        </Box>
      )}

      <Box
        sx={{
          height: listHeight,
          width: width,
          overflow: 'hidden',
          pt: 1,
          background: getActionBarColor(),
        }}>
        <AutoSizer>
          {({ height, width }) => (
            <List
              ref={listRef}
              height={height}
              width={width}
              itemCount={reducedWellList?.length || 0}
              itemSize={getItemSize}
              overscanCount={6}>
              {Row}
            </List>
          )}
        </AutoSizer>
      </Box>
    </Box>
  )
}

export default WellSelectorList
